import { useState, useEffect } from 'react'
import { fetchWithAuth } from '../utils/fetchWithAuth'
import {
  modalPrimaryButtonStyle,
  modalSecondaryButtonStyle
} from '../styles/buttonStyles'

const VehicleDeleteModal = ({ open, vehicle, userId, onClose, onDeleted, t }) => {
  const [loading, setLoading] = useState(false)
  const [pressedBtn, setPressedBtn] = useState(null)
  const [hoverBtn, setHoverBtn] = useState(null)
  const [errorMessage, setErrorMessage] = useState(null)

  useEffect(() => {
    if (!open) return

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        if (errorMessage) {
          setErrorMessage(null)
          return
        }
        onClose()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [open, onClose, errorMessage])

  useEffect(() => {
    if (!open) {
      setHoverBtn(null)
      setPressedBtn(null)
      setErrorMessage(null)
    }
  }, [open])

  if (!open || !vehicle) return null

  const handleDelete = async () => {
    try {
      setLoading(true)

      const res = await fetchWithAuth(`/vehicle/${vehicle.id}`, {
        method: 'DELETE'
      })

      if (!res.ok) {
        let responseBody = null
        try {
          responseBody = await res.json()
        } catch {
          responseBody = null
        }

        if (res.status === 409) {
          const linkedDataMessage = await resolveLinkedDataMessage(
            vehicle,
            userId,
            t,
            responseBody?.error
          )
          setErrorMessage(linkedDataMessage || responseBody?.error || t('common.error'))
          return
        }

        setErrorMessage(responseBody?.error || t('common.error'))
        return
      }

      onDeleted()
    } catch {
      setErrorMessage(t('common.error'))
    } finally {
      setLoading(false)
    }
  }

  return (
    <>
      <div
        onClick={onClose}
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.45)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 10000
        }}
      >
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            background: '#ededed',
            padding: '20px 30px',
            borderRadius: 14,
            border: '2px solid #107c6b',
            boxShadow: '0 12px 28px rgba(0,0,0,0.65)',
            minWidth: 320
          }}
        >
          <h3 style={{ marginTop: 0 }}>
            {t('TRIPLOG.VEHICLES.DELETE_CONFIRM_TITLE')}
          </h3>

          <p>
            {t('TRIPLOG.VEHICLES.DELETE_CONFIRM_MSG')}
          </p>

          <div
            style={{
              display: 'flex',
              justifyContent: 'center',
              gap: 12,
              marginTop: 20
            }}
          >
            <button
              style={modalSecondaryButtonStyle(
                pressedBtn,
                'cancel',
                hoverBtn === 'cancel'
              )}
              onMouseEnter={() => setHoverBtn('cancel')}
              onMouseLeave={() => {
                setHoverBtn(null)
                setPressedBtn(null)
              }}
              onMouseDown={() => setPressedBtn('cancel')}
              onMouseUp={() => setPressedBtn(null)}
              onClick={onClose}
            >
              {t('TRIPLOG.BUTTON.CANCEL')}
            </button>

            <button
              disabled={loading}
              style={{
                ...modalPrimaryButtonStyle(
                  pressedBtn,
                  'confirm',
                  hoverBtn === 'confirm'
                ),
                background: '#c62828',
                borderColor: '#c62828'
              }}
              onMouseEnter={() => setHoverBtn('confirm')}
              onMouseLeave={() => {
                setHoverBtn(null)
                setPressedBtn(null)
              }}
              onMouseDown={() => setPressedBtn('confirm')}
              onMouseUp={() => setPressedBtn(null)}
              onClick={handleDelete}
            >
              {t('TRIPLOG.BUTTON.CONFIRM')}
            </button>
          </div>
        </div>
      </div>

      {errorMessage && (
        <div
          onClick={() => setErrorMessage(null)}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.45)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10001
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#ededed',
              padding: '20px 30px',
              borderRadius: 14,
              border: '2px solid #107c6b',
              boxShadow: '0 12px 28px rgba(0,0,0,0.65)',
              minWidth: 320
            }}
          >
            <h3 style={{ marginTop: 0 }}>
              {t('common.error')}
            </h3>

            <p>{errorMessage}</p>

            <div
              style={{
                display: 'flex',
                justifyContent: 'center',
                marginTop: 20
              }}
            >
              <button
                style={modalPrimaryButtonStyle(
                  pressedBtn,
                  'errorClose',
                  hoverBtn === 'errorClose'
                )}
                onMouseEnter={() => setHoverBtn('errorClose')}
                onMouseLeave={() => {
                  setHoverBtn(null)
                  setPressedBtn(null)
                }}
                onMouseDown={() => setPressedBtn('errorClose')}
                onMouseUp={() => setPressedBtn(null)}
                onClick={() => setErrorMessage(null)}
              >
                {t('TRIPLOG.BUTTON.OK')}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

const resolveLinkedDataMessage = async (vehicle, userId, t, serviceFallbackMessage) => {
  try {
    const [tripsRes, receiptsRes, serviceCostsRes] = await Promise.all([
      fetchWithAuth('/triplog/trips'),
      userId
        ? fetchWithAuth(`/users/${userId}/receipts`)
        : Promise.resolve(null),
      fetchWithAuth('/triplog/service-costs')
    ])

    const trips = tripsRes?.ok ? await tripsRes.json() : []
    const receipts = receiptsRes?.ok ? await receiptsRes.json() : []
    const serviceCosts = serviceCostsRes?.ok ? await serviceCostsRes.json() : []

    const hasTrip = Array.isArray(trips) &&
      trips.some((trip) => String(trip?.vehicleId) === String(vehicle?.id))

    const hasReceipt = Array.isArray(receipts) &&
      receipts.some((receipt) => String(receipt?.vehicle_id) === String(vehicle?.id))

    const hasService = Array.isArray(serviceCosts) &&
      serviceCosts.some((service) => String(service?.vehicleId) === String(vehicle?.id))

    if (hasTrip && hasReceipt) return t('TRIPLOG.ERRORS.HAS_BOTH')
    if (hasTrip) return t('TRIPLOG.ERRORS.HAS_TRIP')
    if (hasReceipt) return t('TRIPLOG.ERRORS.HAS_RECEIPT')
    if (hasService) return t('TRIPLOG.ERRORS.HAS_SERVICE')
    return null
  } catch {
    return null
  }
}

export default VehicleDeleteModal